import * as React from 'react'
import { Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom'
import { CustomerDashboardTabs } from '@app/pages/Customer/CustomerDashboardTabs'
import { NotFound } from '@app/pages/NotFound/NotFound'
import { useDocumentTitle } from '@app/utils/useDocumentTitle'
import { UtilitiesHome } from '@app/pages/Utilities/Home'
import { useKeycloak } from '@react-keycloak/web'
import { YourPatientsHome } from '@app/pages/YourPatients/Home'
import { ProductHome } from '@app/pages/Product/Home'
import { DataHome } from '@app/pages/Data/Home'
import { ApiKeysHome } from '@app/pages/ApiKeys/Home'
import { BayesianModel } from '@app/pages/Model/Bayesian/BayesianModel'
import { DashboardHome } from '@app/pages/Dashboard/Home'
import { CustomerHome } from '@app/pages/Customer/Home'
import { IngredientAnalysisIndex } from '@app/pages/Insights/IngredientAnalysis/Index'
import { BayesianIndex } from '@app/pages/Model/Bayesian/Index'
import { LLMIndex } from '@app/pages/Model/LLM/Index'
import { UserAnalysisIndex } from '@app/pages/Insights/UserAnalysis/Index'
import { ChatAssistantIndex } from '@app/pages/Insights/ChatAssistant/Index'
import { MiniModelHome } from '@app/pages/MiniModel/Home'
import { FormulationAnalysisIndex } from '@app/pages/Insights/FormulationAnalysis/Index'
import { TenantManagementHome } from '@app/pages/TenantManagement/Home'
import { TenantForm } from '@app/pages/TenantManagement/TenantForm'
import {
  PiBookOpenTextLight,
  PiWebhooksLogoLight,
  RiGeminiLine,
  PiUsersThreeLight,
  LuUserCog,
  LuSettings2,
  AiOutlineWechatWork,
  PiToolboxLight,
  PiPaletteLight,
  PiDatabaseLight,
  CiFilter,
  PiPackageLight,
} from '@lib/icons'
import { FormulationAnalysisComparison } from '@app/pages/Insights/FormulationAnalysis/comparison'
import { IngredientAnalysisComparison } from '@app/pages/Insights/IngredientAnalysis/comparison'
import { UserAnalysisComparison } from '@app/pages/Insights/UserAnalysis/comparison'

let routeFocusTimer: number
export interface IAppRoute {
  show?: boolean
  label?: string // Excluding the label will exclude the route from the nav sidebar in AppLayout
  /* eslint-disable @typescript-eslint/no-explicit-any */
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>
  /* eslint-enable @typescript-eslint/no-explicit-any */
  exact?: boolean
  path: string
  title: string
  routes?: undefined
  role?: string
  icon?: React.ReactElement<any, any>
}

export interface IAppRouteGroup {
  show?: boolean
  label: string
  routes: IAppRoute[]
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup

const routes: AppRouteConfig[] = [
  {
    show: false,
    component: DashboardHome,
    exact: true,
    //label: 'Overview',
    path: '/',
    title: 'NØIE Back Office | Dashboard',
    role: 'backoffice-management',
  },

  {
    component: YourPatientsHome,
    exact: true,
    label: 'Your patients',
    path: '/',
    title: 'NØIE Back Office | Your patients',
    role: 'backoffice-your-patients',
  },

  {
    component: DataHome,
    exact: true,
    label: 'Data collection',
    icon: <PiDatabaseLight />,
    path: '/data',
    title: 'NØIE Back Office | Data collection',
    role: 'backoffice-management',
  },
  {
    show: false,
    component: DataHome,
    exact: true,
    path: '/data/:section?/:action?/:id?',
    title: 'NØIE Back Office | Data management',
    role: 'backoffice-management',
  },

  {
    component: MiniModelHome,
    exact: true,
    label: 'Data refinement',
    path: '/data_refinement/main',
    title: 'NØIE Back Office | Data Refinement',
    role: 'backoffice-management',
    icon: <CiFilter />,
  },
  {
    show: false,
    component: MiniModelHome,
    exact: true,
    path: '/data_refinement/:section?/:action?/:id?',
    title: 'NØIE Back Office | Data Refinement',
    role: 'backoffice-management',
  },
  {
    component: BayesianIndex,
    exact: true,
    label: 'Intelligence',
    path: '/model/bayesian',
    title: 'NØIE Back Office | Intelligence',
    role: 'backoffice-management',
    icon: <RiGeminiLine />,
  },
  {
    label: 'Insights',
    routes: [
      {
        component: ChatAssistantIndex,
        exact: true,
        label: 'AI chat',
        path: '/insights/chat-assistant',
        title: 'NØIE Back Office | NØIE chat assistant',
        role: 'backoffice-management',
        icon: <AiOutlineWechatWork />,
      },
      {
        component: UserAnalysisIndex,
        exact: true,
        label: 'Users',
        path: '/insights/profile-analysis',
        title: 'NØIE Back Office | Users',
        role: 'backoffice-management',
        icon: <PiUsersThreeLight />,
      },
      {
        component: UserAnalysisComparison,
        exact: true,
        path: '/insights/profile-analysis/comparison',
        title: 'NØIE Back Office | Users',
        role: 'backoffice-management',
        icon: <PiUsersThreeLight />,
      },
      {
        component: FormulationAnalysisIndex,
        exact: true,
        label: 'Formulations',
        path: '/insights/formulation-analysis',
        title: 'NØIE Back Office | Formulations',
        role: 'backoffice-management',
        icon: <PiBookOpenTextLight />,
      },
      {
        component: FormulationAnalysisComparison,
        exact: true,
        path: '/insights/formulation-analysis/comparison',
        title: 'NØIE Back Office | Formulations',
        role: 'backoffice-management',
        icon: <PiBookOpenTextLight />,
      },
      {
        component: IngredientAnalysisIndex,
        exact: true,
        label: 'Ingredients',
        path: '/insights/ingredient-analysis',
        title: 'NØIE Back Office | Ingredients',
        role: 'backoffice-management',
        icon: <PiPaletteLight />,
      },
      {
        component: IngredientAnalysisComparison,
        exact: true,
        path: '/insights/ingredient-analysis/comparison',
        title: 'NØIE Back Office | Ingredients',
        role: 'backoffice-management',
        icon: <PiPaletteLight />,
      },
    ],
  },

  /*{
    label: 'Model management',
    routes: [
      {
        component: BayesianIndex,
        exact: true,
        label: 'Recommendation models',
        path: '/model/bayesian',
        title: 'NØIE Back Office | Model management',
      },
      {
        component: LLMIndex,
        exact: true,
        label: 'Insights models',
        path: '/model/llms',
        title: 'NØIE Back Office | Model management',
      },
      {
        component: BayesianModel,
        exact: true,
        path: '/model/:section?/:modelName?',
        title: 'NØIE Back Office | Model management',
      },
    ],
  },*/
  {
    show: false,
    component: BayesianModel,
    exact: true,
    path: '/model/:section?/:modelName?',
    title: 'NØIE Back Office | Model management',
    role: 'backoffice-management',
  },

  {
    component: ProductHome,
    exact: true,
    label: 'Products',
    path: '/product/main',
    title: 'NØIE Back Office | Products',
    role: 'backoffice-management',
    icon: <PiPackageLight />,
  },
  {
    show: false,
    component: ProductHome,
    exact: true,
    path: '/product/:section?/:action?/:id?',
    title: 'NØIE Back Office | Products',
    role: 'backoffice-management',
  },

  {
    component: ApiKeysHome,
    exact: true,
    label: 'API keys',
    path: '/api_keys',
    title: 'NØIE Back Office | API Keys',
    role: 'backoffice-management',
    icon: <PiWebhooksLogoLight />,
  },
  {
    show: false,
    component: ApiKeysHome,
    exact: true,
    path: '/api_keys/:action?/:id?',
    title: 'NØIE Back Office | API Keys',
    role: 'backoffice-management',
  },

  {
    show: false,
    component: TenantManagementHome,
    exact: true,
    label: 'Tenant management',
    path: '/system_admin/tenants',
    title: 'NØIE Back Office | Tenant Management',
    role: 'backoffice-management',
    icon: <LuSettings2 />,
  },
  {
    show: false,
    component: TenantForm,
    exact: true,
    path: '/system_admin/tenants/create',
    title: 'NØIE Back Office | Create Tenant',
    role: 'backoffice-management',
  },
  {
    show: false,
    component: TenantForm,
    exact: true,
    path: '/system_admin/tenants/edit/:id',
    title: 'NØIE Back Office | Edit Tenant',
    role: 'backoffice-management',
  },

  {
    show: false,
    label: 'Operations',
    routes: [
      {
        component: CustomerHome,
        exact: true,
        label: 'User management',
        path: '/customer',
        title: 'NØIE Back Office | User management',
        role: 'backoffice-management',
        icon: <LuUserCog />,
      },
      {
        show: false,
        component: CustomerDashboardTabs,
        exact: true,
        path: '/customer/:id/:subid?',
        title: 'NØIE Back Office | User Dashboard',
        role: 'backoffice-management',
      },
      {
        show: false,
        component: CustomerDashboardTabs,
        exact: true,
        path: '/customer/:id/conversations/:conversationId?',
        title: 'NØIE Back Office | User Dashboard',
        role: 'backoffice-management',
      },
      {
        component: UtilitiesHome,
        exact: true,
        label: 'Tools',
        path: '/utilities',
        title: 'NØIE Back Office | Tools',
        role: 'backoffice-management',
        icon: <PiToolboxLight />,
      },
    ],
  },
]

// a custom hook for sending focus to the primary content container
// after a view has loaded so that subsequent press of tab key
// sends focus directly to relevant content
// may not be necessary if https://github.com/ReactTraining/react-router/issues/5210 is resolved
const useA11yRouteChange = () => {
  const { pathname } = useLocation()
  React.useEffect(() => {
    routeFocusTimer = window.setTimeout(() => {
      const mainContainer = document.getElementById('primary-app-container')
      if (mainContainer) {
        mainContainer.focus()
      }
    }, 50)
    return () => {
      window.clearTimeout(routeFocusTimer)
    }
  }, [pathname])
}

const RouteWithTitleUpdates = ({ component: Component, title, ...rest }: IAppRoute) => {
  useA11yRouteChange()
  useDocumentTitle(title)

  function routeWithTitle(routeProps: RouteComponentProps) {
    return <Component {...rest} {...routeProps} />
  }

  const identity = useKeycloak()
  const userRoles = identity.keycloak.tokenParsed?.realm_access?.roles ?? []
  const route = routes.find((p) => p['path'] === rest.path && userRoles.includes(p['role']))
  const role = route && route['role'] ? route['role'] : null

  if (role) {
    if (userRoles.includes(role) && route !== undefined) {
      return <Route render={routeWithTitle} component={route['component']} {...rest} />
    } else {
      return <PageNotFound title="" />
    }
  } else {
    return <Route render={routeWithTitle} {...rest} />
  }
}

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title)
  return <Route component={NotFound} />
}

const flattenedRoutes: IAppRoute[] = routes.reduce((flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])], [] as IAppRoute[])

const AppRoutes = (): React.ReactElement => (
  <Switch>
    {flattenedRoutes.map(({ path, exact, component, title }, idx) => (
      <RouteWithTitleUpdates path={path} exact={exact} component={component} key={idx} title={title} />
    ))}
    <PageNotFound title="404 Page Not Found" />
  </Switch>
)

export { AppRoutes, routes }
